import { useState } from 'react'

import { DangerButton, PrimaryButton, Select, Space } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type User } from '@webapp/platform/iam/entities/User'
import messages from './messages'
import { RemoveHelperTextStyled } from './styles/RemoveHelperTextStyled'
import {
  type ReportAccessSubmitActionEnum,
  ReportAccessSubmitActions,
} from '../../enums'
import { ReportAccessUserSelect } from '../ReportAccessUserSelect'

interface SubmitActionOption {
  value: ReportAccessSubmitActionEnum
  label: string
}

export interface IReportAccessActionRowProps {
  /** ALL-PrimaryCategories mode renders the ADD/REMOVE action select + Submit */
  isAllPrimaryCategoriesMode: boolean
  submitAction: ReportAccessSubmitActionEnum
  onSubmitActionChange: (action: ReportAccessSubmitActionEnum) => void
  onSubmit: (user: User) => void
  isSubmitting: boolean
}

/**
 * Presentational Feature Set subcomponent — the user picker action row.
 * Holds only the transient picker selection; every action fires a Feature
 * Set callback.
 */
export const ReportAccessActionRow = ({
  isAllPrimaryCategoriesMode,
  submitAction,
  onSubmitActionChange,
  onSubmit,
  isSubmitting,
}: IReportAccessActionRowProps) => {
  const intl = useProcurifyIntl()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  const submitActionOptions: SubmitActionOption[] = [
    {
      value: ReportAccessSubmitActions.ADD,
      label: intl.formatMessage(messages.addActionLabel),
    },
    {
      value: ReportAccessSubmitActions.REMOVE,
      label: intl.formatMessage(messages.removeActionLabel),
    },
  ]

  const handleSubmit = () => {
    if (!selectedUser) return
    onSubmit(selectedUser)
    setSelectedUser(null)
  }

  if (!isAllPrimaryCategoriesMode) {
    return (
      <Space size='small'>
        <ReportAccessUserSelect
          value={selectedUser}
          onChange={setSelectedUser}
          isDisabled={isSubmitting}
        />
        <Space direction='horizontal' justify='end'>
          <PrimaryButton
            type='button'
            disabled={!selectedUser}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            {intl.formatMessage(messages.addButtonLabel)}
          </PrimaryButton>
        </Space>
      </Space>
    )
  }

  const isRemoveAction = submitAction === ReportAccessSubmitActions.REMOVE
  const SubmitButtonComponent = isRemoveAction ? DangerButton : PrimaryButton

  return (
    <Space size='small'>
      <Space direction='horizontal' align='start'>
        <Space.Item minWidth={180}>
          <Select<SubmitActionOption>
            options={submitActionOptions}
            value={submitActionOptions.find(
              (option) => option.value === submitAction
            )}
            onChange={(option) => {
              if (option) onSubmitActionChange(option.value)
            }}
            isSearchable={false}
          />
        </Space.Item>
        <Space.Item grow={1}>
          <ReportAccessUserSelect
            value={selectedUser}
            onChange={setSelectedUser}
            isDisabled={isSubmitting}
          />
        </Space.Item>
        <Space.Item>
          {/* Danger styling in REMOVE mode (BHV-024) */}
          <SubmitButtonComponent
            type='button'
            disabled={!selectedUser}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            {intl.formatMessage(messages.submitButtonLabel)}
          </SubmitButtonComponent>
        </Space.Item>
      </Space>

      {isRemoveAction && (
        <RemoveHelperTextStyled>
          {intl.formatMessage(messages.removeHelperText)}
        </RemoveHelperTextStyled>
      )}
    </Space>
  )
}

ReportAccessActionRow.displayName = 'ReportAccessActionRow'
